import { Box, CircularProgress } from "@mui/joy";
import DarkTypography from "components/common/DarkTypography";
import type { ExcursionSMSMessage } from "api/smsTemplates/SMSTemplate.typed";
import { colorTokens } from "styles/globalStyles";
import SmsStatusRow from "./SmsStatusRow";
import { providerDetailLabel, smsStatusMeta } from "./smsStatusMeta";

type ExcursionSmsHistoryProps = {
  messages: ExcursionSMSMessage[];
  loading: boolean;
};

const sentAtFormatter = new Intl.DateTimeFormat("fr-FR", {
  dateStyle: "short",
  timeStyle: "short",
});

function formatSentAt(raw: string): string {
  const date = new Date(raw);

  return Number.isNaN(date.getTime()) ? raw : sentAtFormatter.format(date);
}

function historySubtitle(msg: ExcursionSMSMessage): string {
  const parts = [
    msg.phone,
    msg.template_name?.trim() || "Message libre",
    formatSentAt(msg.sent_at),
  ];
  if (msg.sent_by_name?.trim()) parts.push(`par ${msg.sent_by_name.trim()}`);

  return parts.filter(Boolean).join(" · ");
}

export default function ExcursionSmsHistory({ messages, loading }: ExcursionSmsHistoryProps) {
  if (loading && messages.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size="sm" />
      </Box>
    );
  }

  if (messages.length === 0) {
    return (
      <DarkTypography level="body-sm" colorVariant="muted" sx={{ py: 2, textAlign: "center" }}>
        Aucun SMS envoyé
      </DarkTypography>
    );
  }

  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        minWidth: 0,
        overflow: "auto",
        overscrollBehavior: "contain",
      }}
    >
      <Box
        sx={{
          borderRadius: "8px",
          border: `1px solid ${colorTokens.border.default}`,
          backgroundColor: colorTokens.bg.surfaceElevated,
          p: { xs: 0.75, sm: 1 },
          minWidth: 0,
        }}
      >
        {messages.map((msg) => (
          <SmsStatusRow
            key={msg.id}
            status={msg.status}
            title={msg.recipient_name?.trim() || msg.phone}
            subtitle={historySubtitle(msg)}
            error={
              smsStatusMeta(msg.status).rank === 0
                ? providerDetailLabel(msg.status_details)
                : undefined
            }
          />
        ))}
      </Box>
    </Box>
  );
}
